import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceRespSchema,
  IPaymentInstructionSchema,
  PaymentInstructionSchema,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { updateInvoicePaymentInstructions } from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { invoiceServiceQueryKeys } from '../../api/invoiceService';
import {
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
  MText,
} from '../../components/Monetize';
import { useConfirmModal } from '../../services/confirmModal';

interface EditPaymentInstructionsModalProps {
  invoice: IInvoiceRespSchema;
  isOpen: boolean;
  handleClose: () => void;
}
export const EditPaymentInstructionsModal = ({
  invoice,
  isOpen,
  handleClose,
}: EditPaymentInstructionsModalProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const defaultValues = useMemo(
    () => ({
      accountNumber: invoice?.paymentInstruction?.accountNumber ?? '',
      bankName: invoice?.paymentInstruction?.bankName ?? '',
      routingNumber: invoice?.paymentInstruction?.routingNumber ?? '',
      swiftCode: invoice?.paymentInstruction?.swiftCode ?? '',
    }),
    [invoice?.paymentInstruction],
  );

  const {
    handleSubmit,
    control,
    formState: { dirtyFields, isDirty, isValid, errors },
  } = useForm<IPaymentInstructionSchema>({
    resolver: zodResolver(PaymentInstructionSchema),
    mode: 'onChange',
    defaultValues,
  });

  const clearInvoiceCache = async () => {
    await queryClient.invalidateQueries({
      queryKey: [...invoiceServiceQueryKeys.base],
    });
  };

  // Determine if all payment instruction fields are empty
  const isAllEmpty = useMemo(() => {
    return Object.values(defaultValues).every((value) => !value);
  }, [defaultValues]);

  const onSubmit = async (formData: IPaymentInstructionSchema) => {
    setLoading(true);
    try {
      await updateInvoicePaymentInstructions(invoice.id, invoice.account.id, {
        paymentInstruction: {
          ...formData,
        },
      });
      await clearInvoiceCache();

      handleClose();
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    const onYes = async () => {
      setLoading(true);
      setModalLoading(true);

      try {
        await updateInvoicePaymentInstructions(invoice.id, invoice.account.id, {
          paymentInstruction: null,
        });
        await clearInvoiceCache();
        handleClose();
      } catch (err) {
        handleApiErrorToast(err);
      } finally {
        setLoading(false);
      }
      closeModal();
      setModalLoading(true);
    };
    showConfirmModal({
      title: `Are you sure you want to remove the payment instructions?`,
      description:
        'This action cannot be undone, you can always add payment instructions again.',
      onYes,
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Remove',
      noButton: 'Cancel',
    });
  };

  return (
    <MCenterModal
      modalContentProps={
        { 'data-testid': 'edit-payment-instructions-modal' } as any
      }
      size="lg"
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      modalTitle={'Update Payment Instructions'}
      renderFooter={() => (
        <MStack
          gap={4}
          direction="row"
          align="space-between"
          justify="end"
          flex={1}
        >
          <MButton
            variant="tertiary"
            onClick={handleRemove}
            minW="auto"
            isDisabled={isAllEmpty}
            isLoading={loading}
          >
            Remove
          </MButton>
          <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
            <MButton
              isDisabled={loading}
              onClick={handleClose}
              variant="cancel"
              minW="auto"
            >
              Cancel
            </MButton>
            <MButton
              variant="primary"
              isDisabled={!isDirty || !isValid}
              onClick={handleSubmit(onSubmit)}
              isLoading={loading}
              minW="auto"
            >
              Save
            </MButton>
          </MStack>
        </MStack>
      )}
    >
      <MGrid gap={2}>
        <MText fontSize="sm" fontStyle={'italic'} mb={2}>
          Edit the bank account details used for wire or ACH transfers. These
          changes will update the payment instructions shown on the invoice.
        </MText>
        <MGridItem>
          <MFormField error={errors.bankName} label="Bank Name" isRequired>
            <Controller
              name="bankName"
              control={control}
              render={({ field }) => (
                <MInput
                  variant={dirtyFields.bankName ? 'unsaved' : 'outline'}
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem>
          <MFormField
            error={errors.accountNumber}
            label="Account Number"
            isRequired
          >
            <Controller
              name="accountNumber"
              control={control}
              render={({ field }) => (
                <MInput
                  variant={dirtyFields.accountNumber ? 'unsaved' : 'outline'}
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem>
          <MFormField
            error={errors.routingNumber}
            label="Routing Number"
            isRequired
          >
            <Controller
              name="routingNumber"
              control={control}
              render={({ field }) => (
                <MInput
                  variant={dirtyFields.routingNumber ? 'unsaved' : 'outline'}
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>
        <MGridItem>
          <MFormField error={errors.swiftCode} label="Swift Code" isRequired>
            <Controller
              name="swiftCode"
              control={control}
              render={({ field }) => (
                <MInput
                  variant={dirtyFields.swiftCode ? 'unsaved' : 'outline'}
                  {...field}
                />
              )}
            />
          </MFormField>
        </MGridItem>
      </MGrid>
    </MCenterModal>
  );
};
